import type { ListUserSavedSearchesResponse, UserSavedSearch } from "@/modules/jobAds/client/api/types"

import { useQuery } from "@tanstack/react-query"

import { GET_USER_SAVED_SEARCHES_QUERY_KEY } from "@/modules/jobAds/client/api/constants"
import { getUserSavedSearches } from "@/modules/jobAds/client/api/getUserSavedSearches"
import { JOB_ALERT_BATCH_B } from "@/modules/jobAds/client/constants/flags"
import { computeUserSavedSearches } from "@/modules/jobAds/client/utils/computeUserSavedSearches"
import { logger } from "@/modules/monitoring/logger"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

export type GetUserSavedSearches = {
  data: UserSavedSearch[]
  isSuccess: boolean
  isLoading: boolean
  isError: boolean
}

export const useGetUserSavedSearches = (): GetUserSavedSearches => {
  const { dataCurrentUser } = useCurrentUserFetch()
  const isJobAlertBatchBEnabled = useFlag(JOB_ALERT_BATCH_B)
  const isLogged = !!dataCurrentUser?.isLogged

  const {
    data,
    isPending: isLoading,
    isError,
    isSuccess,
  } = useQuery<ListUserSavedSearchesResponse, Error>({
    enabled: isLogged,
    queryFn: () => getUserSavedSearches({ isJobAlertBatchBEnabled }),
    queryKey: [GET_USER_SAVED_SEARCHES_QUERY_KEY, isJobAlertBatchBEnabled],
  })

  try {
    const savedSearches = computeUserSavedSearches(data?.savedSearches)

    return {
      data: savedSearches,
      isError,
      isLoading,
      isSuccess,
    }
  } catch (error) {
    logger.error(`Could not parse attributes from saved searches : ${JSON.stringify(data?.savedSearches)}`)

    return {
      data: [],
      isError: true,
      isLoading,
      isSuccess: false,
    }
  }
}
